import { useEffect } from "react";
import { useLocation } from "react-router";
import { getAnalyticsMeasurementId, type AnalyticsConsentState } from "../consent";

type GoogleAnalyticsManagerProps = {
  consent: AnalyticsConsentState;
  hasAnalyticsConfigured: boolean;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GOOGLE_ANALYTICS_SCRIPT_ID = "mutzur-google-analytics";
const initializedMeasurementIds = new Set<string>();

const deniedConsent = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

const grantedConsent = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "granted",
} as const;

function ensureGtag() {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    // Match the official gtag bootstrap shape so queued commands are
    // interpreted correctly once gtag.js is ready.
    window.gtag = function gtag(..._args: unknown[]) {
      window.dataLayer?.push(arguments);
    };
  }

  return window.gtag;
}

function injectGoogleAnalyticsScript(measurementId: string) {
  const existingScript = document.getElementById(GOOGLE_ANALYTICS_SCRIPT_ID);

  if (existingScript instanceof HTMLScriptElement) {
    if (existingScript.src.includes(measurementId)) {
      return;
    }

    existingScript.remove();
  }

  const script = document.createElement("script");
  script.id = GOOGLE_ANALYTICS_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

function deleteCookie(name: string) {
  const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  const hostnameParts = window.location.hostname.split(".");
  const domains = new Set(["", window.location.hostname]);

  for (let index = 0; index < hostnameParts.length - 1; index += 1) {
    domains.add(`.${hostnameParts.slice(index).join(".")}`);
  }

  for (const domain of domains) {
    const domainPart = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=; ${expires}; path=/; SameSite=Lax${domainPart}`;
  }
}

function clearGoogleAnalyticsCookies() {
  const cookieNames = document.cookie
    .split(";")
    .map((entry) => entry.trim().split("=")[0] ?? "")
    .filter((name) => name === "_ga" || name === "_gid" || name.startsWith("_ga_"));

  for (const name of new Set(cookieNames)) {
    deleteCookie(name);
  }
}

function setGoogleAnalyticsDisabled(measurementId: string, disabled: boolean) {
  const disableKey = `ga-disable-${measurementId}`;
  (window as unknown as Window & Record<string, boolean | undefined>)[disableKey] = disabled;
}

export function GoogleAnalyticsManager({
  consent,
  hasAnalyticsConfigured,
}: GoogleAnalyticsManagerProps) {
  const location = useLocation();
  const measurementId = getAnalyticsMeasurementId();

  useEffect(() => {
    if (!hasAnalyticsConfigured || !measurementId) {
      return;
    }

    if (consent === "granted") {
      setGoogleAnalyticsDisabled(measurementId, false);

      const gtag = ensureGtag();
      gtag("consent", "default", deniedConsent);
      gtag("consent", "update", grantedConsent);

      if (!initializedMeasurementIds.has(measurementId)) {
        gtag("js", new Date());
        gtag("config", measurementId, {
          send_page_view: false,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        });
        initializedMeasurementIds.add(measurementId);
      }

      injectGoogleAnalyticsScript(measurementId);
      return;
    }

    setGoogleAnalyticsDisabled(measurementId, true);

    if (typeof window.gtag === "function") {
      const gtag = ensureGtag();
      gtag("consent", "default", deniedConsent);
      gtag("consent", "update", deniedConsent);
    }

    clearGoogleAnalyticsCookies();
  }, [consent, hasAnalyticsConfigured, measurementId]);

  useEffect(() => {
    if (!hasAnalyticsConfigured || !measurementId || consent !== "granted") {
      return;
    }

    const gtag = ensureGtag();
    gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: `${location.pathname}${location.search}${location.hash}`,
      page_title: document.title,
    });
  }, [
    consent,
    hasAnalyticsConfigured,
    location.hash,
    location.pathname,
    location.search,
    measurementId,
  ]);

  return null;
}
